import React, { useEffect, useState } from "react";
import styles from "./ContactForm.module.css";
import { useForm, Controller } from "react-hook-form";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import validator from "validator";
import useGeoLocation from "@/services/useGeoLocation";
import { ChevronDown } from "lucide-react";
import { CmsAllCousesList } from "@/services/CmsService";
import { useRouter } from "next/router";
import { submitFormData } from "@/services/LeadService";
import { toast } from "react-hot-toast";

interface FormData {
  name: string;
  email: string;
  phone: string;
  Programme_Of_Interest: string;
  qualification: string;
  mode: string;
}

const qualifications = [
  "Doctorate",
  "Master’s Degree",
  "Hons Degree",
  "Post Graduation",
  "Bachelor’s Degree",
  "Diploma",
  "Higher Certificate",
  "Matric (Bachelor Pass)",
  "Matric",
  "Pursuing Matric",
  "Grade 12 and Below",
  "Higher National Diploma",
  "National Diploma",
  "Ordinary National Diploma",
  "KCSE (Matric)",
  "SSC",
  "BA",
];

interface Program {
  field_of_interest: string;
  program_name: string;
  slug: string;
  program_brochure?: {
    brochure_pdf_link?: {
      url: string;
    };
  };
}

const ContactForm = () => {
  const router = useRouter();
  const [mobileNumber, setMobileNumber] = useState("");
  const [btnDisable, setBtnDisable] = useState(false);
  const [phoneNumberError, setPhoneNumberError] = useState<string>("");
  const geoLocationData: any = useGeoLocation();
  const [selectedProgram, setSelectedProgram] = useState("");
  const [slugs, setSlugs] = useState<Program[]>([]);

  const { asPath } = router;
  const pathArray = asPath.split("/").filter((path) => path);
  const slugPath = pathArray[0];

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    watch,
  } = useForm<FormData>();

  const programmeOfInterest = watch("Programme_Of_Interest");
  const Qualification = watch("qualification");

  useEffect(() => {
    const fetchCourseData = async () => {
      try {
        const response = await CmsAllCousesList();
        const sortedData = response?.data?.sort((a: any, b: any) => {
          if (a.program_name < b.program_name) {
            return -1;
          }
          if (a.program_name > b.program_name) {
            return 1;
          }
          return 0;
        });
        setSlugs(sortedData || []);

        const matchingSlug = response?.data.find(
          (slug: Program) => slug.slug === slugPath
        );
        if (matchingSlug) {
          setSelectedProgram(matchingSlug.field_of_interest);
        }
      } catch (error) {
        console.error("Error fetching course data:", error);
      }
    };

    fetchCourseData();
  }, [slugPath]);

  const onSubmit = async (data: FormData) => {
    if (!mobileNumber || !isValidPhoneNumber(mobileNumber)) {
      return;
    }
    setBtnDisable(true);
    try {
      await submitFormData(
        {
          ...data,
          course: data.Programme_Of_Interest,
          qualification: data.qualification,
          page_url: window.location.href,
          City: geoLocationData?.city,
          country: geoLocationData?.countryName,
          is_otp_verified: 0,
        },
        router
      );
      toast.success("Form Submitted Successfully ✅", {
        position: "top-right",
        style: {
          backgroundColor: "#176f1d",
          color: "#fff",
          fontWeight: "bold",
          fontSize: "16px",
          whiteSpace: "nowrap",
          overflow: "visible",
          minWidth: "fit-content",
        },
      });
    } catch (error: any) {
      console.error(
        "Sorry, there was an error submitting your form. Please try again later."
      );
      console.error(error);
    } finally {
      reset();
      setMobileNumber("");
      setBtnDisable(false);
    }
  };

  return (
    <div className={styles.contactContainer}>
      <div className="container">
        <div className={styles.topHeadings}>
          <p className={styles.topHeader}>Have Questions? Ask Us!</p>
          <h2 className={styles.subHeader}>Get Guidance from Our Team</h2>
        </div>
        <form
          action=""
          onSubmit={handleSubmit(onSubmit)}
          className={styles.padding}
        >
          <div className={styles.inputGroup}>
            <div className={styles.field}>
              <input
                autoComplete="off"
                {...register("name", { required: "Name is required" })}
                placeholder="Enter Full Name"
              />
              {errors.name && (
                <p className={styles.error}>{errors.name.message}</p>
              )}
            </div>
            <div className={styles.field}>
              <input
                autoComplete="off"
                {...register("email", { required: "Email is required" })}
                placeholder="Enter Email"
                type="email"
              />
              {errors.email && (
                <p className={styles.error}>{errors.email.message}</p>
              )}
            </div>
            <div
              className={`${styles.fieldInPut} ${
                phoneNumberError ? styles.marginBottom : ""
              } `}
            >
              <div>
                <Controller
                  name="phone"
                  control={control}
                  rules={{
                    required: "Phone number is required",
                  }}
                  render={({ field }) => (
                    <PhoneInput
                      {...field}
                      value={mobileNumber}
                      international
                      countryCallingCodeEditable={false}
                      onChange={(value) => {
                        const phoneNumber = value ? value.toString() : "";
                        const isValid = validator.isMobilePhone(
                          phoneNumber || ""
                        );
                        setMobileNumber(phoneNumber as string);
                        field.onChange(phoneNumber);

                        if (phoneNumber.replace(/[^0-9]/g, "").length >= 4) {
                          if (isValid) {
                            setMobileNumber(phoneNumber);
                            setPhoneNumberError("");
                          } else {
                            setPhoneNumberError("Valid phone number required");
                          }
                        } else {
                          setPhoneNumberError("");
                        }
                      }}
                      defaultCountry={geoLocationData?.countryCode}
                    />
                  )}
                />
              </div>
              <div className={`${styles.error} ${styles.phoneError}`}>
                {phoneNumberError && (
                  <div className={`${styles.error}`}>
                    <small className={styles.error}>{phoneNumberError}</small>
                  </div>
                )}

                {errors?.phone && !phoneNumberError && (
                  <div className={`${styles.error}`}>
                    <small className={styles.error}>
                      {errors.phone.message}
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className={`${styles.inputGroup} ${styles.groupTwo}`}>
            <div className={`${styles.field} ${styles.marginTop}`}>
              <span className={styles.dropdownIcon}>
                <ChevronDown />
              </span>
              <select
                {...register("Programme_Of_Interest", {
                  required: selectedProgram ? false : "*Course is required",
                })}
                value={programmeOfInterest}
              >
                <option value="" hidden>
                  Select Programme
                </option>
                {slugs.map((slug, index) => (
                  <option key={index} value={slug?.field_of_interest}>
                    {slug.program_name}
                  </option>
                ))}
              </select>
              {errors.Programme_Of_Interest && (
                <p className={`${styles.error}`}>
                  {errors.Programme_Of_Interest.message}
                </p>
              )}
            </div>
            <div className={styles.field}>
              <span className={styles.dropdownIcon}>
                <ChevronDown />
              </span>
              <select
                {...register("qualification", {
                  required: "*Qualification is required",
                })}
                value={Qualification}
              >
                <option value="" hidden>
                  Select Highest Qualification
                </option>
                {qualifications.map((qualification, index) => (
                  <option key={index} value={qualification}>
                    {qualification}
                  </option>
                ))}
              </select>
              {errors.qualification && (
                <p className={styles.error}>{errors.qualification.message}</p>
              )}
            </div>
          </div>
          <div className={styles.submitButton}>
            <button
              type="submit"
              className={`${styles.submitBtn} ${
                btnDisable ? styles.disabledBtn : ""
              }`}
              disabled={btnDisable}
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
export default ContactForm;
